import { type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import type { StreamRefusal } from '../lib/appendLogStream';
import { useMe } from '../lib/session';
import { otherOrgCount } from './RunStreamRefusal';

// WHY this session is not on screen, when the stream REFUSED rather than dropped.
// The session-side sibling of `<RunStreamRefusal/>`, over the same
// `StreamRefusal` and the same `isAnsweredRefusal` predicate — two views, one
// rule, so they cannot drift on what counts as an answer.
//
// `<AuditSession/>` read only `status`, and `useAppendLogStream` collapses a
// foreign session id, a 503 and a dropped socket into one `error`. Two things
// followed, both wrong:
//
//   - a 404 got `<ErrorState/>`'s "Couldn't load this session." plus a Try again
//     that re-issues the same 404 for ever. A 404 IS AN ANSWER — the API folds
//     "not yours" and "no such session" into one on purpose — so it renders as
//     its own line with no retry;
//   - `token === null` resolves to `done` WITHOUT sending a request, and the
//     fallthrough painted `audit.empty` — "No events yet.", a claim about a
//     session nobody asked about. That is the same lie `fleet.run_signed_out`
//     was written to stop, one route over.
//
// Every other refusal still falls through to `<ErrorState/>` and keeps its
// Retry: a 503 and a dropped socket are worth retrying, which is the whole
// distinction this draws.

export type SessionStreamRefusalProps = {
  readonly refusal: StreamRefusal;
  /** Injectable for tests; the route passes its own `props.token`. */
  readonly token: string | null;
};

export function SessionStreamRefusal(props: SessionStreamRefusalProps): JSX.Element {
  const me = useMe(props.token);
  return (
    <div class="empty-state" role="status">
      <Switch
        fallback={
          <>
            <p class="empty-state__message">{t('audit_session.signed_out')}</p>
            <p class="timeline__meta">{t('audit_session.signed_out_desc')}</p>
          </>
        }
      >
        {/* No cross-org hint on a 403, for the reason `<RunStreamRefusal/>`
            records: a refused tenant claim is about the CALLER's membership of
            the org they asked as, and says nothing about where the session is.
            The 404 is the one that folds "not yours" in. */}
        <Match when={props.refusal.kind === 'http' && props.refusal.status === 403}>
          <p class="empty-state__message">{t('audit_session.denied')}</p>
        </Match>
        <Match when={props.refusal.kind === 'http'}>
          <p class="empty-state__message">{t('audit_session.not_found')}</p>
          {/* Evidence the client already holds (`/v1/me`), never an authorization:
              the destination re-asks the server either way. */}
          <Show when={otherOrgCount(me.data) > 0}>
            <p class="timeline__meta">{t('audit_session.not_found_other_orgs')}</p>
          </Show>
        </Match>
      </Switch>
    </div>
  );
}
